<template>
  <section v-if="guidance" :class="['approval-gate-card', `is-${tone}`]" role="status">
    <div class="approval-gate-card__head">
      <div>
        <span class="approval-gate-card__eyebrow">审批关卡</span>
        <strong>{{ guidance.gateName || '待处理节点' }}</strong>
      </div>
      <el-tag :type="tagType" effect="plain" size="small">{{ guidance.status || '待处理' }}</el-tag>
    </div>

    <div class="approval-gate-card__grid">
      <div>
        <span>当前不能继续</span>
        <strong>{{ guidance.blockedAction || '等待本节点处理后才能继续' }}</strong>
      </div>
      <div>
        <span>通过后解锁</span>
        <strong>{{ guidance.nextAction || '进入下一业务节点' }}</strong>
      </div>
      <div>
        <span>责任角色</span>
        <strong>{{ responsibleRoles }}</strong>
      </div>
      <div>
        <span>当前处理人</span>
        <strong>{{ approverSummary }}</strong>
      </div>
    </div>

    <el-alert
        v-if="guidance.configurationIssue"
        :closable="false"
        :title="guidance.configurationIssue"
        class="approval-gate-card__alert"
        show-icon
        type="error"
    />

    <div v-if="recentRecord" class="approval-gate-card__recent">
      <span>最近处理</span>
      <strong>{{ recentRecordSummary }}</strong>
    </div>
  </section>
</template>

<script>
/**
 * 审批关卡完整卡片：所有信息均由服务端返回，页面不根据角色或状态自行推断审批人。
 */
export default {
  name: 'ApprovalGateCard',
  props: {
    guidance: {
      type: Object,
      default: null,
    },
    recentRecord: {
      type: Object,
      default: null,
    },
  },
  computed: {
    normalizedStatus() {
      return String(this.guidance && this.guidance.status || '').toUpperCase()
    },
    tone() {
      if (this.guidance && this.guidance.configurationIssue) return 'danger'
      if (this.normalizedStatus.includes('REJECT') || this.normalizedStatus.includes('驳回')) return 'danger'
      if (this.normalizedStatus.includes('APPROVED') || this.normalizedStatus.includes('通过')) return 'success'
      return 'warning'
    },
    tagType() {
      return {danger: 'danger', success: 'success', warning: 'warning'}[this.tone]
    },
    responsibleRoles() {
      const roles = Array.from(new Set((this.guidance.responsibleRoleNames || []).filter(Boolean)))
      return roles.length ? roles.join('、') : '待配置责任角色'
    },
    approverSummary() {
      const approvers = (this.guidance.approvers || []).filter(item => item && item.displayName)
      const total = Number(this.guidance.totalApproverCount || approvers.length)
      if (!approvers.length || total <= 0) return '暂无可处理账号'
      const names = approvers.map(item => item.displayName).join('、')
      return total > approvers.length ? `${names}等，共 ${total} 人` : `${names}，共 ${total} 人`
    },
    recentRecordSummary() {
      const record = this.recentRecord || {}
      const status = record.statusName || record.status || record.decisionName || record.decision || '已处理'
      const handler = record.approverName || record.handledBy || record.reviewerName || '处理人未记录'
      const time = record.approvedAt || record.handledAt || record.reviewedAt || record.createdAt || '时间未记录'
      const comment = record.comment || record.approvalComment || record.remark || '未填写意见'
      return `${status} · ${handler} · ${time} · ${comment}`
    },
  },
}
</script>

<style scoped>
.approval-gate-card {
  padding: 16px;
  border: 1px solid #f0c36a;
  border-left: 4px solid #e6a23c;
  border-radius: 8px;
  background: #fffaf0;
}

.approval-gate-card.is-danger {
  border-color: #f5b5b5;
  border-left-color: #f56c6c;
  background: #fff6f6;
}

.approval-gate-card.is-success {
  border-color: #b8dfca;
  border-left-color: #2e9d62;
  background: #f4fbf7;
}

.approval-gate-card__head,
.approval-gate-card__head > div {
  display: flex;
  align-items: center;
  gap: 10px;
}

.approval-gate-card__head {
  justify-content: space-between;
}

.approval-gate-card__head strong {
  color: #1f2d3d;
  font-size: 15px;
}

.approval-gate-card__eyebrow {
  color: #805500;
  font-size: 12px;
  font-weight: 600;
}

.approval-gate-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
  margin-top: 14px;
}

.approval-gate-card__grid > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.approval-gate-card__grid span,
.approval-gate-card__recent > span {
  color: #6b7280;
  font-size: 12px;
}

.approval-gate-card__grid strong,
.approval-gate-card__recent strong {
  color: #303133;
  font-size: 13px;
  line-height: 1.6;
}

.approval-gate-card__alert,
.approval-gate-card__recent {
  margin-top: 12px;
}

.approval-gate-card__recent {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px dashed rgba(128, 85, 0, 0.25);
}

@media (max-width: 720px) {
  .approval-gate-card__grid {
    grid-template-columns: 1fr;
  }
}
</style>
